import { useEffect, useState } from "react";
import { apiGet } from "../lib/api";
import { Card, Pill, Sub } from "../components/ui";

type Node = {
  dataset: string;
  source: string;
  tables: string[];
  readers: string[];
  lastRun: { status: string; reason: string | null; at: string } | null;
  degraded: boolean;
};

export default function FlowsScreen() {
  const [data, setData] = useState<{ nodes: Node[]; degradedCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Node | null>(null);

  useEffect(() => {
    apiGet<{ nodes: Node[]; degradedCount: number }>("/api/admin/flows")
      .then(setData)
      .catch((e) => setError(String(e.message ?? e)));
  }, []);

  if (error) return <p className="text-sm text-brand">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl text-ink">Flows</h1>
        {data.degradedCount > 0 ? (
          <Pill tone="amber">{data.degradedCount} stream{data.degradedCount === 1 ? "" : "s"} degraded</Pill>
        ) : (
          <Pill tone="ok">all healthy</Pill>
        )}
        <span className="text-sm text-neutral-400">where every dataset comes from, what it becomes, who reads it</span>
      </div>

      <Card className="flex-1">
        <div className="mb-3 hidden lg:grid lg:grid-cols-3 lg:gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Sources → tables</div>
          <div />
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Readers</div>
        </div>
        <div className="flex flex-col gap-2">
          {data.nodes.map((n) => (
            <button
              key={n.dataset}
              onClick={() => setSelected(n)}
              className={`grid grid-cols-1 items-center gap-2 rounded-lg border px-3.5 py-3 text-left text-sm sm:grid-cols-[1fr_auto] sm:gap-3 sm:py-2.5 ${
                n.degraded ? "border-red-200 bg-red-50/40" : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100"
              }`}
            >
              <div>
                <div className="font-medium text-ink">
                  {n.dataset}
                  {n.degraded && <span className="ml-2 text-xs text-brand">degraded</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-neutral-500">{n.source}</div>
                <div className="mt-1 flex flex-col gap-0.5 text-xs text-neutral-400 sm:block">
                  <span>→ {n.tables.length ? n.tables.join(", ") : "no table yet"}</span>
                  {n.readers.length > 0 && (
                    <span className="flex items-center gap-1">
                      <ArrowDivider /> read by {n.readers.join(", ")}
                    </span>
                  )}
                </div>
              </div>
              {n.lastRun && (
                <Pill tone={n.lastRun.status === "ok" ? "ok" : n.lastRun.status === "partial" ? "amber" : "red"}>
                  {n.lastRun.status}
                </Pill>
              )}
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-neutral-400">Click a node for its last run and why, if it failed.</p>
      </Card>

      {selected && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30 p-4 sm:p-6" onClick={() => setSelected(null)}>
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg text-ink">{selected.dataset}</h3>
            <p className="mt-1 text-sm text-neutral-500">{selected.source}</p>
            <p className="mt-3 text-sm text-ink">Tables: {selected.tables.join(", ") || "—"}</p>
            <p className="mt-1 text-sm text-ink">Readers: {selected.readers.join(", ") || "—"}</p>
            {selected.lastRun && (
              <p className="mt-3 text-sm text-neutral-600">
                Last run <Sub>{selected.lastRun.at}</Sub>: {selected.lastRun.status}
                {selected.lastRun.reason && <span className="block text-brand">{selected.lastRun.reason}</span>}
              </p>
            )}
            <button className="mt-4 min-h-[44px] text-sm font-semibold text-brand" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ArrowDivider() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
